import React from 'react';
import {
  BuildingDataContainer,
  BuildingRow,
  BuildingLabel,
  BuildingValue,
  SkeletonRow,
  SkeletonLine,
  BuildingPicker,
  BuildingPickerLabel,
  BuildingSelect,
  BuildingPickerHint,
  BuildingEmpty,
} from './building-data.styled';
import { BuildingData as Building } from '../../interfaces/building-interfaces';
import { StateSwitch } from '../../motion';

interface BuildingDataProps {
  buildingData?: Building | null;
  buildings?: Building[];
  loading?: boolean;
  selectable?: boolean;
  onSelectBuilding?: (buildingId: string) => void;
}

export const BuildingData: React.FC<BuildingDataProps> = ({
  buildingData,
  buildings = [],
  loading = false,
  selectable = false,
  onSelectBuilding,
}) => {
  const requiresSelection = selectable && buildings.length > 1 && !buildingData;
  const pickerHintId = 'building-picker-hint';

  const renderSkeleton = () => (
    <>
      {[68, 84, 72, 60].map((w, i) => (
        <SkeletonRow key={i}>
          <SkeletonLine w="40%" />
          <SkeletonLine w={`${w}%`} />
        </SkeletonRow>
      ))}
    </>
  );

  // ---- A ROW WITH NOTHING IN IT IS NOT A ROW ----
  //
  // Reported 2026-08-13: "no address and phone number is displayed in the
  // sidebar" — and both were rendered, as a LABEL followed by nothing, because
  // the building genuinely has neither. An empty labelled row reads as a screen
  // that failed to load its data; an absent row reads as a building that has
  // none, which is the truth. The City row already worked this way.
  //
  // THE ADDRESS HAS TWO HOMES. Migration 035 added the resolved location record
  // (`formatted_address` — what the map service returned) beside the original
  // free-text `address`. A building created through the location picker fills
  // the first and a building typed by hand fills the second, so reading only one
  // of them shows a blank for half the estate. The resolved one wins where both
  // exist: it is the normalised form.
  const addressLine =
    buildingData?.formatted_address?.trim() || buildingData?.address?.trim() || '';

  const rows: { label: string; value: string }[] = [
    { label: 'Building Name', value: buildingData?.property_name?.trim() || '' },
    { label: 'Address', value: addressLine },
    { label: 'City', value: buildingData?.city?.trim() || '' },
    { label: 'Phone Number', value: buildingData?.phone_number?.trim() || '' },
  ].filter(row => row.value !== '');

  const renderRows = () => (
    <>
      {rows.map(row => (
        <BuildingRow key={row.label}>
          <BuildingLabel>{row.label}</BuildingLabel>
          <BuildingValue>{row.value}</BuildingValue>
        </BuildingRow>
      ))}
      {/* A building with a name and nothing else is a real state — one created
          in the onboarding wizard, which asks for a name and a unit count. Say
          so, rather than leaving a panel that looks like it is still loading. */}
      {rows.length <= 1 && (
        <BuildingRow>
          <BuildingValue>
            No address or phone number recorded yet. Add them on the building.
          </BuildingValue>
        </BuildingRow>
      )}
    </>
  );

  return (
    <BuildingDataContainer>
      {selectable && (
        <BuildingPicker>
          <BuildingPickerLabel htmlFor="building-picker">
            Active building
          </BuildingPickerLabel>
          <BuildingSelect
            id="building-picker"
            value={buildingData?.id || ''}
            disabled={loading || buildings.length < 2}
            aria-describedby={requiresSelection ? pickerHintId : undefined}
            onChange={event => onSelectBuilding?.(event.target.value)}
          >
            <option value="" disabled>
              {loading
                ? 'Loading buildings…'
                : buildings.length === 0
                ? 'No buildings available'
                : 'Select a building…'}
            </option>
            {buildings.map(building => (
              <option key={building.id} value={building.id}>
                {building.property_name}
              </option>
            ))}
          </BuildingSelect>
          {requiresSelection && (
            <BuildingPickerHint id={pickerHintId} role="status">
              Choose a building before opening a desk action.
            </BuildingPickerHint>
          )}
        </BuildingPicker>
      )}

      {/* Skeleton → rows → empty are three fills of the same panel, crossfaded
          in place (StateSwitch, mode wait) so the sidebar never pops. The
          floor reserves roughly the skeleton's height so the panel does not
          shrink under the one-line empty state and jump back. */}
      <StateSwitch
        state={loading ? 'loading' : buildingData ? 'ready' : 'empty'}
        minHeight={96}
      >
        {loading ? (
          renderSkeleton()
        ) : buildingData ? (
          renderRows()
        ) : (
          <BuildingEmpty>
            {buildings.length === 0
              ? 'No buildings are available for this account.'
              : 'Select a building to see its desk details.'}
          </BuildingEmpty>
        )}
      </StateSwitch>
    </BuildingDataContainer>
  );
};
